import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../api/client";
import AccountSettingSelect from "../../../components/AccountSettingSelect";
import { renderEditionOption } from "../../../components/dropdownOptionRenderers";
import { canChooseFoilOption, isFoilOptionDisabled, normalizeFoilSelection } from "./foilOptionRules";
import TraderModal from "./TraderModal";
import "./TraderCardModalLayout.css";
import {
  WANTS_ANY_EDITION_VALUE,
  WANTS_ANY_VALUE,
  WANTS_BOOLEAN_OPTIONS,
  WANTS_CONDITION_OPTIONS,
  WANTS_LANGUAGE_OPTIONS,
  getConditionColor,
  getLanguageCountryCode,
  getWantsConditionLabel,
  getWantsLanguageLabel
} from "../../../wantsSettingsOptions";

const BINARY_BOOLEAN_OPTIONS = WANTS_BOOLEAN_OPTIONS.filter((option) => option.value !== WANTS_ANY_VALUE);
const fieldLabelStyle = { display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" };

function getInitialFormState({ defaultLanguage = "en", defaultCondition = "NM", defaultFoil = "false", defaultAltered = "false", defaultSigned = "false", defaultAnyEdition = false } = {}) {
  const defaultFoilState = getWantBooleanFormState(defaultFoil);
  const defaultAlteredState = getWantBooleanFormState(defaultAltered);
  const defaultSignedState = getWantBooleanFormState(defaultSigned);

  return {
    qty: 1,
    edition: "",
    scryfallId: "",
    language: defaultLanguage,
    condition: defaultCondition,
    foil: defaultFoilState.boolValue,
    altered: defaultAlteredState.boolValue,
    signed: defaultSignedState.boolValue,
    anyEdition: defaultAnyEdition,
    anyLanguage: false,
    anyCondition: false,
    anyFoil: defaultFoilState.anyValue,
    anyAltered: defaultAlteredState.anyValue,
    anySigned: defaultSignedState.anyValue
  };
}

function getWantBooleanSelectValue(value, anyValue) {
  return anyValue ? WANTS_ANY_VALUE : String(Boolean(value));
}

function getWantBooleanFormState(value) {
  if (value === WANTS_ANY_VALUE) {
    return { anyValue: true, boolValue: false };
  }

  return {
    anyValue: false,
    boolValue: value === "true"
  };
}

function renderLanguageValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any language</span>;
  }

  const countryCode = getLanguageCountryCode(value);

  return (
    <>
      <span className={`fi fi-${countryCode} account-setting-select-preview-flag`} aria-hidden="true" />
      <span className="account-setting-select-preview-label">{getWantsLanguageLabel(value)}</span>
    </>
  );
}

function renderConditionValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any condition</span>;
  }

  const conditionCode = String(value || "NM").toUpperCase();

  return (
    <>
      <span
        className="account-setting-select-preview-badge"
        style={{
          background: getConditionColor(conditionCode),
          color: conditionCode === "GD" ? "#111" : "#fff"
        }}
      >
        {conditionCode}
      </span>
      <span className="account-setting-select-preview-label">{getWantsConditionLabel(conditionCode)}</span>
    </>
  );
}

function renderBooleanValue(value, options) {
  return <span className="account-setting-select-preview-label">{options.find((option) => option.value === value)?.label || value}</span>;
}

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function getPrintingReleaseTime(printing) {
  const timestamp = Date.parse(printing?.releasedAt || "");
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
}

function getRepresentativePrintingPrice(printing, { requireFoil = false } = {}) {
  const rawPrice = requireFoil ? printing?.priceFoil : printing?.priceTrend;

  if (rawPrice === null || rawPrice === undefined) {
    return null;
  }

  if (typeof rawPrice === "string" && rawPrice.trim() === "") {
    return null;
  }

  const numericPrice = Number(rawPrice);

  return Number.isFinite(numericPrice) && numericPrice >= 0 ? numericPrice : null;
}

function selectRepresentativePrinting(printings, { requireFoil = false } = {}) {
  if (!Array.isArray(printings) || printings.length === 0) {
    return null;
  }

  const eligiblePrintings = requireFoil
    ? printings.filter((printing) => Boolean(printing?.foil))
    : printings;
  const candidatePrintings = eligiblePrintings.length > 0 ? eligiblePrintings : printings;
  const pricedPrintings = candidatePrintings.filter((printing) => getRepresentativePrintingPrice(printing, { requireFoil }) !== null);

  if (pricedPrintings.length === 0) {
    return candidatePrintings[0] || null;
  }

  return [...pricedPrintings].sort((left, right) => {
    const priceDelta = getRepresentativePrintingPrice(left, { requireFoil }) - getRepresentativePrintingPrice(right, { requireFoil });

    if (priceDelta !== 0) {
      return priceDelta;
    }

    const releaseDelta = getPrintingReleaseTime(left) - getPrintingReleaseTime(right);

    if (releaseDelta !== 0) {
      return releaseDelta;
    }

    const collectorDelta = normalizeLookupValue(left?.collectorNumber).localeCompare(normalizeLookupValue(right?.collectorNumber));

    if (collectorDelta !== 0) {
      return collectorDelta;
    }

    return normalizeLookupValue(left?.setCode).localeCompare(normalizeLookupValue(right?.setCode));
  })[0] || candidatePrintings[0] || null;
}

export default function AddCardsModal({
  isOpen,
  onClose,
  onAdded,
  userId,
  title = "Add Cards",
  searchApiBasePath = "/api/traders",
  submitUrl = "/api/traders/add",
  submitPayload = null,
  defaultLanguage = "en",
  defaultCondition = "NM",
  defaultFoil = "false",
  defaultAltered = "false",
  defaultSigned = "false",
  showLanguageField = false,
  enableWantWildcards = false
}) {
  const searchFieldRef = useRef(null);
  const dropdownRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCardName, setSelectedCardName] = useState("");
  const [printings, setPrintings] = useState([]);
  const [form, setForm] = useState(() => getInitialFormState({ defaultLanguage, defaultCondition, defaultFoil, defaultAltered, defaultSigned, defaultAnyEdition: enableWantWildcards }));
  const [searchLoading, setSearchLoading] = useState(false);
  const [printingsLoading, setPrintingsLoading] = useState(false);
  const [setMetadata, setSetMetadata] = useState({});
  const [searchError, setSearchError] = useState("");
  const [printingsError, setPrintingsError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSuggestions([]);
      setSelectedCardName("");
      setPrintings([]);
      setForm(getInitialFormState({ defaultLanguage, defaultCondition, defaultFoil, defaultAltered, defaultSigned, defaultAnyEdition: enableWantWildcards }));
      setSearchLoading(false);
      setPrintingsLoading(false);
      setSearchError("");
      setPrintingsError("");
      setSubmitError("");
      setIsSubmitting(false);
      setIsDropdownOpen(false);
      setDropdownStyle(null);
    }
  }, [defaultAltered, defaultCondition, defaultFoil, defaultLanguage, defaultSigned, enableWantWildcards, isOpen]);

  useEffect(() => {
    if (!isOpen || !isDropdownOpen) {
      return undefined;
    }

    const updateDropdownPosition = () => {
      const element = searchFieldRef.current;

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(180, window.innerHeight - rect.bottom - 24),
        zIndex: 1400
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isOpen, isDropdownOpen, suggestions.length, searchLoading, searchError]);

  useEffect(() => {
    if (!isOpen || !isDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (searchFieldRef.current?.contains(event.target) || dropdownRef.current?.contains(event.target)) {
        return;
      }

      setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, isDropdownOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery.length < 3 || trimmedQuery === selectedCardName) {
      setSuggestions([]);
      setSearchLoading(false);
      setSearchError("");
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError("");

        const response = await apiFetch(
          `${searchApiBasePath}/search/cards?q=${encodeURIComponent(trimmedQuery)}`,
          {
            userId,
            signal: controller.signal
          }
        );
        const json = await response.json();

        if (!response.ok) {
          throw new Error(json.error || "Could not search cards");
        }

        setSuggestions(Array.isArray(json.suggestions) ? json.suggestions : []);
        setIsDropdownOpen(true);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        setSuggestions([]);
        setSearchError(error.message || "Could not search cards");
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, searchApiBasePath, searchQuery, selectedCardName, userId]);

  const selectedPrinting = useMemo(() => {
    if (enableWantWildcards && form.anyEdition) {
      return selectRepresentativePrinting(printings, { requireFoil: form.foil && !form.anyFoil });
    }

    return printings.find((printing) => printing.scryfallId === form.scryfallId) || null;
  }, [enableWantWildcards, form.anyEdition, form.anyFoil, form.foil, form.scryfallId, printings]);
  const foilOptionContext = useMemo(() => ({
    selectedPrinting,
    printings,
    anyEdition: enableWantWildcards && form.anyEdition
  }), [enableWantWildcards, form.anyEdition, printings, selectedPrinting]);
  const previewImage = selectedPrinting?.imageNormal || selectedPrinting?.image || "";
  const canChooseFoil = canChooseFoilOption(foilOptionContext);

  const renderFinishSelect = ({ label, value, onChange, options, disableTrueOption = false, containerClassName = "" }) => (
    <div className={containerClassName}>
      <label style={fieldLabelStyle}>
        {label}
      </label>
      <AccountSettingSelect
        title={label}
        value={value}
        options={options.map((option) => ({
          ...option,
          disabled: (disableTrueOption && option.value === "true" && !canChooseFoil)
            || isFoilOptionDisabled(option.value, foilOptionContext)
        }))}
        onChange={(nextValue) => onChange({ target: { value: nextValue } })}
        renderSelectedValue={(nextValue) => renderBooleanValue(nextValue, options)}
        renderOption={(nextValue) => renderBooleanValue(nextValue, options)}
      />
    </div>
  );

  useEffect(() => {
    const setCodes = [...new Set(printings.map((printing) => normalizeLookupValue(printing.setCode)).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (isCancelled) {
          return;
        }

        setSetMetadata((current) => ({
          ...current,
          ...(data.sets || {})
        }));
      })
      .catch((error) => {
        console.error("Set metadata error:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [printings, setMetadata, userId]);

  useEffect(() => {
    const normalizedFoilState = normalizeFoilSelection({ foil: form.foil, anyFoil: form.anyFoil }, foilOptionContext);

    if (normalizedFoilState.foil !== form.foil || normalizedFoilState.anyFoil !== form.anyFoil) {
      setForm((current) => ({
        ...current,
        foil: normalizedFoilState.foil,
        anyFoil: normalizedFoilState.anyFoil
      }));
      return;
    }

    if (!canChooseFoil && form.foil) {
      setForm((current) => ({ ...current, foil: false }));
    }
  }, [canChooseFoil, foilOptionContext, form.anyFoil, form.foil]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setSubmitError("");

    if (selectedCardName && value !== selectedCardName) {
      setSelectedCardName("");
      setPrintings([]);
      setPrintingsError("");
      setForm((current) => ({
        ...getInitialFormState({ defaultLanguage, defaultCondition, defaultFoil, defaultAltered, defaultSigned, defaultAnyEdition: enableWantWildcards }),
        qty: current.qty,
        language: current.language,
        condition: current.condition,
        foil: current.foil,
        altered: current.altered,
        signed: current.signed,
        anyEdition: enableWantWildcards,
        anyLanguage: current.anyLanguage,
        anyCondition: current.anyCondition,
        anyFoil: current.anyFoil,
        anyAltered: current.anyAltered,
        anySigned: current.anySigned
      }));
    }

    setIsDropdownOpen(true);
  };

  const handleSelectCard = async (cardName) => {
    try {
      setSelectedCardName(cardName);
      setSearchQuery(cardName);
      setSuggestions([]);
      setIsDropdownOpen(false);
      setPrintings([]);
      setPrintingsError("");
      setSubmitError("");
      setPrintingsLoading(true);

      const response = await apiFetch(
        `${searchApiBasePath}/search/prints?card=${encodeURIComponent(cardName)}`,
        { userId }
      );
      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || "Could not load card editions");
      }

      const nextPrintings = Array.isArray(json.prints) ? json.prints : [];
      setPrintings(nextPrintings);

      if (nextPrintings.length > 0) {
        const preferredPrinting = enableWantWildcards
          ? selectRepresentativePrinting(nextPrintings, { requireFoil: form.foil && !form.anyFoil })
          : (form.foil && !form.anyFoil
            ? nextPrintings.find((printing) => Boolean(printing.foil)) || nextPrintings[0]
            : nextPrintings[0]);

        setForm((current) => ({
          ...current,
          edition: preferredPrinting?.edition || "",
          scryfallId: preferredPrinting?.scryfallId || "",
          anyEdition: enableWantWildcards
        }));
      }
    } catch (error) {
      setPrintingsError(error.message || "Could not load card editions");
    } finally {
      setPrintingsLoading(false);
    }
  };

  const handleEditionChange = (scryfallId) => {
    if (scryfallId === WANTS_ANY_EDITION_VALUE) {
      const preferredPrinting = selectRepresentativePrinting(printings, { requireFoil: form.foil && !form.anyFoil });

      setForm((current) => ({
        ...current,
        edition: preferredPrinting?.edition || current.edition,
        scryfallId: preferredPrinting?.scryfallId || current.scryfallId,
        anyEdition: true
      }));
      return;
    }

    const nextPrinting = printings.find((printing) => printing.scryfallId === scryfallId);

    setForm((current) => ({
      ...current,
      scryfallId,
      edition: nextPrinting?.edition || "",
      anyEdition: false
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedCardName || !form.scryfallId) {
      setSubmitError("Choose a card and edition first.");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");

      const payload = typeof submitPayload === "function"
        ? submitPayload({
            selectedCardName,
            form,
            selectedPrinting
          })
        : {
            card: selectedCardName,
            qty: Math.max(1, Number(form.qty) || 1),
            edition: form.edition,
            language: form.language,
            condition: form.condition,
            foil: form.foil,
            altered: form.altered,
            signed: form.signed,
            anyEdition: form.anyEdition,
            anyLanguage: form.anyLanguage,
            anyCondition: form.anyCondition,
            anyFoil: form.anyFoil,
            anyAltered: form.anyAltered,
            anySigned: form.anySigned,
            useTrend: true,
            scryfallId: form.scryfallId
          };

      const response = await apiFetch(submitUrl, {
        userId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || "Could not add card");
      }

      if (onAdded) {
        onAdded(json);
      }

      onClose();
    } catch (error) {
      setSubmitError(error.message || "Could not add card");
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldShowDropdown = isDropdownOpen && (suggestions.length > 0 || searchLoading || searchError || searchQuery.trim().length >= 3);
  const editionSelectValue = enableWantWildcards && form.anyEdition
    ? WANTS_ANY_EDITION_VALUE
    : form.scryfallId;
  const dropdown = shouldShowDropdown && dropdownStyle
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            ...dropdownStyle,
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: "14px",
            boxShadow: "0 18px 30px rgba(15, 23, 42, 0.12)",
            overflowY: "auto"
          }}
        >
          {searchLoading && (
            <div style={{ padding: "12px 14px", color: "#64748b", fontSize: "14px" }}>
              Searching...
            </div>
          )}
          {!searchLoading && searchError && (
            <div style={{ padding: "12px 14px", color: "#b91c1c", fontSize: "14px" }}>
              {searchError}
            </div>
          )}
          {!searchLoading && !searchError && suggestions.length === 0 && searchQuery.trim().length >= 3 && (
            <div style={{ padding: "12px 14px", color: "#64748b", fontSize: "14px" }}>
              No cards found.
            </div>
          )}
          {!searchLoading && !searchError && suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => handleSelectCard(suggestion)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 14px",
                border: "none",
                background: "#fff",
                cursor: "pointer",
                fontSize: "14px",
                borderTop: index === 0 ? "none" : "1px solid #f1f5f9"
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <TraderModal isOpen={isOpen} onClose={onClose} title={title} width="860px">
      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gap: "18px" }}>
          <div ref={searchFieldRef} style={{ position: "relative" }}>
            <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
              Search for a card
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => handleSearchChange(event.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
              placeholder="Start typing at least 3 letters..."
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                fontSize: "15px",
                boxSizing: "border-box"
              }}
            />
          </div>
          {dropdown}

          {selectedCardName && (
            <div className={`trader-card-modal-section${previewImage ? " trader-card-modal-section--with-image" : ""}`}>
              {previewImage && (
                <div className="trader-card-modal-media">
                  <div className="app-card-image-frame">
                    <img
                      src={previewImage}
                      alt={selectedCardName}
                    />
                  </div>
                  {selectedPrinting && (
                    <div className="trader-card-modal-price" style={{ fontSize: "13px", color: "#64748b" }}>
                      <div className="trader-card-modal-price-line">Trend €{selectedPrinting.priceTrend || "-"}</div>
                      <div className="trader-card-modal-price-line">Foil trend €{selectedPrinting.priceFoil || "-"}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="trader-card-modal-fields">
                <div className="trader-card-modal-row">
                  <div className="trader-card-modal-field trader-card-modal-field--qty">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Qty
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={form.qty}
                      onChange={(event) => setForm((current) => ({ ...current, qty: event.target.value }))}
                      className="trader-card-modal-input"
                    />
                  </div>

                  <div className="trader-card-modal-field trader-card-modal-field--card">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Card
                    </label>
                    <input
                      type="text"
                      value={selectedCardName}
                      readOnly
                      className="trader-card-modal-input trader-card-modal-input--readonly"
                    />
                  </div>
                </div>

                <div className="trader-card-modal-row">
                  <div className="trader-card-modal-field trader-card-modal-field--edition">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Edition
                    </label>
                    <AccountSettingSelect
                      title="Edition"
                      value={editionSelectValue || ""}
                      onChange={handleEditionChange}
                      disabled={printingsLoading || printings.length === 0}
                      options={[
                        ...(printings.length === 0 ? [{ value: "", label: "No editions found", disabled: true }] : []),
                        ...(enableWantWildcards && printings.length > 0 ? [{ value: WANTS_ANY_EDITION_VALUE, label: "Any" }] : []),
                        ...printings.map((printing) => ({
                          value: printing.scryfallId,
                          label: `${printing.edition} (${printing.setCode.toUpperCase()})${printing.collectorNumber ? ` · #${printing.collectorNumber}` : ""}`,
                          setCode: printing.setCode,
                          setMetadata: setMetadata[normalizeLookupValue(printing.setCode)],
                          showSetSymbol: true
                        }))
                      ]}
                      renderSelectedValue={(nextValue, option) => renderEditionOption(nextValue, option, "No editions found")}
                      renderOption={(nextValue, option) => renderEditionOption(nextValue, option)}
                    />
                    {printingsLoading && <div style={{ marginTop: "8px", fontSize: "13px", color: "#64748b" }}>Loading editions...</div>}
                    {printingsError && <div style={{ marginTop: "8px", fontSize: "13px", color: "#b91c1c" }}>{printingsError}</div>}
                  </div>
                </div>

                <div className="trader-card-modal-row">
                  <div className="trader-card-modal-field trader-card-modal-field--half">
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
                      Condition
                    </label>
                    <AccountSettingSelect
                      title="Condition"
                      value={enableWantWildcards && form.anyCondition ? WANTS_ANY_VALUE : form.condition}
                      options={[
                        ...(enableWantWildcards ? [{ value: WANTS_ANY_VALUE, label: "Any" }] : []),
                        ...WANTS_CONDITION_OPTIONS
                      ]}
                      onChange={(nextValue) => setForm((current) => ({
                        ...current,
                        condition: nextValue === WANTS_ANY_VALUE ? current.condition : nextValue,
                        anyCondition: enableWantWildcards && nextValue === WANTS_ANY_VALUE
                      }))}
                      renderSelectedValue={renderConditionValue}
                      renderOption={renderConditionValue}
                    />
                  </div>

                  {showLanguageField ? (
                    <div className="trader-card-modal-field trader-card-modal-field--half">
                      <label style={fieldLabelStyle}>
                        Language
                      </label>
                      <AccountSettingSelect
                        title="Language"
                        value={enableWantWildcards && form.anyLanguage ? WANTS_ANY_VALUE : form.language}
                        options={[
                          ...(enableWantWildcards ? [{ value: WANTS_ANY_VALUE, label: "Any" }] : []),
                          ...WANTS_LANGUAGE_OPTIONS
                        ]}
                        onChange={(nextValue) => setForm((current) => ({
                          ...current,
                          language: nextValue === WANTS_ANY_VALUE ? current.language : nextValue,
                          anyLanguage: enableWantWildcards && nextValue === WANTS_ANY_VALUE
                        }))}
                        renderSelectedValue={renderLanguageValue}
                        renderOption={renderLanguageValue}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="trader-card-modal-row">
                  {enableWantWildcards ? (
                    <>
                      {renderFinishSelect({
                        label: "Foil",
                        value: getWantBooleanSelectValue(form.foil, form.anyFoil),
                        onChange: (event) => {
                          const nextState = getWantBooleanFormState(event.target.value);
                          setForm((current) => ({
                            ...current,
                            foil: nextState.boolValue,
                            anyFoil: nextState.anyValue
                          }));
                        },
                        options: WANTS_BOOLEAN_OPTIONS,
                        disableTrueOption: true,
                        containerClassName: "trader-card-modal-field trader-card-modal-field--quarter"
                      })}

                      {renderFinishSelect({
                        label: "Altered",
                        value: getWantBooleanSelectValue(form.altered, form.anyAltered),
                        onChange: (event) => {
                          const nextState = getWantBooleanFormState(event.target.value);
                          setForm((current) => ({
                            ...current,
                            altered: nextState.boolValue,
                            anyAltered: nextState.anyValue
                          }));
                        },
                        options: WANTS_BOOLEAN_OPTIONS,
                        containerClassName: "trader-card-modal-field trader-card-modal-field--quarter"
                      })}

                      {renderFinishSelect({
                        label: "Signed",
                        value: getWantBooleanSelectValue(form.signed, form.anySigned),
                        onChange: (event) => {
                          const nextState = getWantBooleanFormState(event.target.value);
                          setForm((current) => ({
                            ...current,
                            signed: nextState.boolValue,
                            anySigned: nextState.anyValue
                          }));
                        },
                        options: WANTS_BOOLEAN_OPTIONS,
                        containerClassName: "trader-card-modal-field trader-card-modal-field--quarter"
                      })}
                    </>
                  ) : (
                    <>
                      {renderFinishSelect({
                        label: "Foil",
                        value: String(form.foil),
                        onChange: (event) => setForm((current) => ({ ...current, foil: event.target.value === "true" })),
                        options: BINARY_BOOLEAN_OPTIONS,
                        disableTrueOption: true,
                        containerClassName: "trader-card-modal-field trader-card-modal-field--quarter"
                      })}

                      {renderFinishSelect({
                        label: "Altered",
                        value: String(form.altered),
                        onChange: (event) => setForm((current) => ({ ...current, altered: event.target.value === "true" })),
                        options: BINARY_BOOLEAN_OPTIONS,
                        containerClassName: "trader-card-modal-field trader-card-modal-field--quarter"
                      })}

                      {renderFinishSelect({
                        label: "Signed",
                        value: String(form.signed),
                        onChange: (event) => setForm((current) => ({ ...current, signed: event.target.value === "true" })),
                        options: BINARY_BOOLEAN_OPTIONS,
                        containerClassName: "trader-card-modal-field trader-card-modal-field--quarter"
                      })}
                    </>
                  )}

                </div>
              </div>
            </div>
          )}

          {submitError && (
            <div style={{ padding: "12px 14px", borderRadius: "12px", background: "#fef2f2", color: "#b91c1c", fontSize: "14px" }}>
              {submitError}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              onClick={onClose}
              className="app-button-base"
              style={{ borderRadius: "10px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedCardName || !form.scryfallId || isSubmitting || printingsLoading}
              className="app-button-base"
              style={{
                borderRadius: "10px",
                border: "none",
                background: !selectedCardName || !form.scryfallId || isSubmitting || printingsLoading ? "#94a3b8" : "#0f766e",
                color: "#fff",
                cursor: !selectedCardName || !form.scryfallId || isSubmitting || printingsLoading ? "not-allowed" : "pointer"
              }}
            >
              {isSubmitting ? "Adding..." : "Add card"}
            </button>
          </div>
        </div>
      </form>
    </TraderModal>
  );
}